import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'default' | 'destructive' | 'warning';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  fullWidth?: boolean;
  // Accesibilidad mejorada
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      children,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none cursor-pointer disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-offset-2';

    const variantClasses = {
      // PMO #23361: primary-600 con texto blanco da 4,10:1 (AA pide 4,5). primary-700 da 5,85:1; el hover sube a primary-800
      // (8,55:1) para seguir distinguiendose del reposo. El anillo de foco pasa de primary-500 (2,97:1 contra el offset
      // blanco, bajo los 3:1 de WCAG 1.4.11) a primary-600 (4,10:1): MUSE, #3031.
      primary:
        'bg-primary-700 text-white hover:bg-primary-800 focus-visible:ring-primary-600',
      // GH#3033: todo anillo de foco >= 3:1 contra el offset blanco (WCAG 1.4.11). secondary-500 2,44 -> 600 3,32;
      // outline/ghost primary-500 2,97 -> 600 4,10; default gray-400 2,60 -> 500 4,84; warning yellow-500 1,91 ->
      // 700 4,92 (el 600 da 2,93 y tampoco llega). danger (3,76) y destructive (3,82) ya pasaban. Lo ata la spec.
      // GH#3042: el TEXTO en reposo también cumple AA (4,5:1). secondary: blanco sobre 600 daba 3,32 -> 700 (4,65), hover
      // 800 (6,72). warning: blanco sobre yellow-600 daba 2,93 y el blanco solo llega en yellow-700, que ya no se lee
      // como aviso; pasa a texto gray-900 sobre yellow-400 (11,32), hover yellow-500 (9,27). Lo ata la spec 3042.
      // GH#3050: con texto blanco, secondary-700 quedaba pegado a primary-700 (cociente 1,26, menos que el reposo->hover
      // de primary, 1,46) y los dos niveles se leían iguales. Pasa a tono suave con texto oscuro: se distingue por forma
      // de relleno (4,87 contra primary-700). Texto secondary-900: 8,25:1 en reposo y 6,88:1 en hover (secondary-800
      // bajaba a 4,67 en hover). El anillo secondary-600 (3,32 contra el offset blanco, #3033) no cambia.
      secondary:
        'bg-secondary-100 text-secondary-900 hover:bg-secondary-200 focus-visible:ring-secondary-600',
      outline:
        'border border-gray-300 bg-transparent hover:bg-gray-50 focus-visible:ring-primary-600',
      ghost: 'hover:bg-gray-100 focus-visible:ring-primary-600',
      danger:
        'bg-error-600 text-white hover:bg-error-700 focus-visible:ring-error-500',
      default:
        'bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-500',
      destructive:
        'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
      warning:
        'bg-yellow-400 text-gray-900 hover:bg-yellow-500 focus-visible:ring-yellow-700',
    };

    const sizeClasses = {
      sm: 'h-7 px-2.5 text-xs',
      md: 'h-8 px-3 text-xs',
      lg: 'h-10 px-4 text-sm',
      icon: 'h-8 w-8 p-0',
    };

    const widthClasses = fullWidth ? 'w-full' : '';

    return (
      <button
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          widthClasses,
          className
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="mr-1.5 h-3.5 w-3.5 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {!loading && LeftIcon && <LeftIcon className="mr-1.5 h-3.5 w-3.5" />}

        {children}

        {!loading && RightIcon && <RightIcon className="ml-1.5 h-3.5 w-3.5" />}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
export default Button;
